import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, controlToastState } from "risa-oasis-ui_v2";
import { v4 as uuidv4 } from "uuid";
import { checkClaimsForCsv } from "../../api/postCall/checkClaimsForCsv";
import { prepareClaimsExcel } from "../../api/postCall/prepareClaimsExcel";
import DragDropUpload from "../../components/dragDropUpload/dragDropUpload";
import { fetchNycbsClaimsPaOrdersFromFirebase } from "../../redux/slice/ordersSlice/nycbsClaimsPaSlice";
import { AppDispatch } from "../../redux/store/store";
import { InfoIcon } from "../../svg/info-icon";
import { logError } from "../../utils/customLogger";
import ClaimsTable from "./components/claimsTable/claimsTable";

const generateShortId = () => uuidv4().replace(/-/g, "").slice(0, 15);

const Claims = () => {
  const [fileListLength, setFileListLength] = useState(0);
  const [uniqueId, setUniqueId] = useState(generateShortId());
  const [downloadedURL, setDownloadedURL] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [reportDownload, setReportDownload] = useState(false);
  const [operationSuccess, setOperationSuccess] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    fileListLength === 0 && setUniqueId(generateShortId());
    setReportDownload(false);
    setOperationSuccess(false);
  }, [fileListLength]);

  useEffect(() => {
    dispatch(fetchNycbsClaimsPaOrdersFromFirebase());
  }, []);

  const checkClaimsPatientEligibilityViaApi = async () => {
    setLoading(true);
    setReportDownload(false);
    const storageRef = `gs://prior--backen-prod-svc-u4g8.appspot.com/claims_check/${uniqueId}`;

    const body = {
      url: storageRef,
      org_id: "nycbs_claims_pa",
      job_id: uniqueId,
    };

    const response = await checkClaimsForCsv(body);
    if (response.hasError) {
      controlToastState("check-claims-failure");
    } else {
      controlToastState("check-claims-success");
      fetchClaimsReportDetails();
      setReportDownload(true);
    }
    setLoading(false);
  };

  const fetchClaimsReportDetails = async () => {
    const body = {
      org_id: "nycbs_claims_pa",
      job_id: uniqueId,
    };
    const response = await prepareClaimsExcel(body);
    if (response.hasError) {
      logError(response.response, "Error while fetching claims report details");
    }
    setOperationSuccess(true);
  };

  return (
    <div className="claims-check flex h-full w-full flex-col gap-4 overflow-auto p-4">
      <div className="claims-check-header text-h11 font-bold">
        Claims Processing
      </div>
      <div className="claims-check-body flex flex-col gap-4">
        <div className="claims-check-body__content text-body font-regular">
          Please upload a file or drag and drop a file here to process claims
          data. (only .csv file)
        </div>
        <div className="claims-check-body__uploader">
          <DragDropUpload
            id={"claims-check"}
            allowedFileTypes={[".csv"]}
            filePath={`/claims_check/${uniqueId}`}
            fileListLength={(length) => setFileListLength(length)}
            downloadedURL={(url) => setDownloadedURL(url)}
            onSuccessfullyUploaded={operationSuccess}
            maxFileSize={10 * 1024 * 1024}
          />
        </div>
        <div className="claims-check-body__button flex w-full justify-end gap-4">
          {loading && (
            <>
              <div className="claims-check--info mr-auto flex items-center gap-2 text-body font-normal italic">
                <InfoIcon />
                Processing claims would take couple of minutes. Please do not
                refresh the page.
              </div>
            </>
          )}
          <Button
            disabled={
              downloadedURL.length === 0 ||
              downloadedURL[0] === "" ||
              loading ||
              reportDownload
                ? true
                : false
            }
            children={loading ? "Processing..." : "Process Claims"}
            onClick={checkClaimsPatientEligibilityViaApi}
            buttonType={"secondary"}
            size={"medium"}
          />
        </div>
      </div>
      <div className="claims-check-table flex-1">
        <ClaimsTable />
      </div>
    </div>
  );
};

export default Claims;
